// Shared hook (TS-2.5.02, Engine-ish utility - pure state machine, no HTTP). Guards an
// in-app navigation a dirty form's own screen initiates (a "Back to projects" link, a row click
// elsewhere in the shell) behind a confirm step, and warns on a browser-level unload (tab close/
// refresh) too. `react-router-dom`'s `useBlocker`/`usePrompt` require a data router
// (`createBrowserRouter`), and this app is scaffolded on a plain `<BrowserRouter>` (decisions.md
// D-223's own header lists `Routes`/`Route`/`NavLink`/`Navigate`/`useLocation` as the stable
// surface actually used) - so this hook implements the same "are you sure?" shape without one,
// deliberately out of scope for the browser Back/Forward buttons themselves (a `popstate`
// interception is unreliable across browsers and was not attempted here).
import { useCallback, useEffect, useState } from "react";

export interface UseDirtyNavigationGuardResult {
  readonly isDirty: boolean;
  readonly setDirty: (next: boolean) => void;
  /** True while a guarded navigation is waiting on the confirm dialog. */
  readonly confirmPending: boolean;
  /** Wrap any navigation trigger (link click, button) in this; it defers `action` behind a
   *  confirm step when dirty, and runs it immediately otherwise. */
  readonly guardedNavigate: (action: () => void) => void;
  readonly confirmDiscard: () => void;
  readonly cancelDiscard: () => void;
}

export function useDirtyNavigationGuard(): UseDirtyNavigationGuardResult {
  const [isDirty, setDirty] = useState(false);
  const [pendingAction, setPendingAction] = useState<(() => void) | null>(null);

  useEffect(() => {
    if (!isDirty) return;

    const handler = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };

    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [isDirty]);

  const guardedNavigate = useCallback(
    (action: () => void) => {
      if (isDirty) {
        setPendingAction(() => action);
      } else {
        action();
      }
    },
    [isDirty],
  );

  const confirmDiscard = useCallback(() => {
    const action = pendingAction;
    setPendingAction(null);
    setDirty(false);
    action?.();
  }, [pendingAction]);

  const cancelDiscard = useCallback(() => {
    setPendingAction(null);
  }, []);

  return {
    isDirty,
    setDirty,
    confirmPending: pendingAction !== null,
    guardedNavigate,
    confirmDiscard,
    cancelDiscard,
  };
}
